/**
 * Direct Messages Page
 * 
 * List all DM conversations and start new ones.
 */

import { useState, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { 
  MessageSquare, 
  Search, 
  Plus,
  Circle,
  Loader2,
  ChevronRight,
  X,
} from 'lucide-react'
import { cn, formatRelativeTime, getInitials, getAvatarColor, getPresenceConfig } from '../lib/utils'
import { channelsApi, usersApi, dmApi, type Channel, type User } from '../lib/api'
import { useTeamsStore } from '../store/teamsStore'

interface MessagesPageProps {
  showCreate?: boolean
}

export default function MessagesPage({ showCreate = false }: MessagesPageProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState('')
  const [showNewDM, setShowNewDM] = useState(showCreate || !!searchParams.get('user'))
  const { setChannels } = useTeamsStore()
  
  // Fetch DM channels
  const { data: channels = [], isLoading } = useQuery({
    queryKey: ['teams-channels'],
    queryFn: channelsApi.list,
    onSuccess: setChannels,
  })
  
  // Filter to only DMs
  const dmChannels = useMemo(() => {
    let result = channels.filter(c => c.type === 'direct')
    
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      result = result.filter(c => 
        c.other_user?.name?.toLowerCase().includes(q) ||
        c.other_user?.email?.toLowerCase().includes(q)
      )
    }
    
    return result
  }, [channels, searchQuery])

  return (
    <div className="h-full bg-slate-50 dark:bg-slate-900">
      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">Messages</h1>
            <p className="text-slate-500 dark:text-slate-400">
              {dmChannels.length} conversations
            </p>
          </div>
          <button
            onClick={() => setShowNewDM(true)}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Message
          </button>
        </div>
        
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conversations..."
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          />
        </div>
        
        {/* DM List */}
        {isLoading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-violet-500 mb-3" />
            <p className="text-sm text-slate-400">Loading messages...</p>
          </div>
        ) : dmChannels.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <MessageSquare className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <p className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">No messages yet</p>
            <p className="text-sm text-slate-400 mb-4">Start a conversation with a team member</p>
            <button
              onClick={() => setShowNewDM(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Start Conversation
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {dmChannels.map((channel) => (
              <DMCard 
                key={channel.id} 
                channel={channel} 
                onClick={() => navigate(`/messages/${channel.id}`)} 
              />
            ))}
          </div>
        )}
      </div>
      
      {/* New DM Modal */}
      {showNewDM && (
        <NewDMModal
          initialUserId={searchParams.get('user') ? parseInt(searchParams.get('user')!) : undefined}
          onClose={() => {
            setShowNewDM(false)
            if (showCreate) navigate('/messages')
          }}
          onSelect={(channel) => navigate(`/messages/${channel.id}`)}
        />
      )}
    </div>
  )
}

// DM Card Component
function DMCard({ channel, onClick }: { channel: Channel, onClick: () => void }) {
  const user = channel.other_user
  const presenceConfig = getPresenceConfig(user?.is_online ? 'online' : 'offline')
  
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-violet-300 dark:hover:border-violet-700 hover:shadow-md transition-all text-left"
    >
      <div className="relative">
        <div className={cn(
          "w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-semibold bg-gradient-to-br",
          getAvatarColor(user?.name || '')
        )}>
          {getInitials(user?.name || '', user?.email)}
        </div>
        <div className={cn(
          "absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ring-2 ring-white dark:ring-slate-800",
          presenceConfig.color
        )} />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-slate-800 dark:text-white truncate">{user?.name || 'Unknown'}</h3>
          <span className={cn(
            "text-xs px-1.5 py-0.5 rounded",
            user?.is_online 
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
              : "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
          )}>
            {user?.is_online ? 'Online' : 'Offline'}
          </span>
        </div>
        {channel.last_message && (
          <p className="text-sm text-slate-500 truncate mt-1">
            {channel.last_message.content}
          </p>
        )}
      </div>
      
      <div className="flex flex-col items-end gap-2">
        {channel.last_message && (
          <span className="text-xs text-slate-400">
            {formatRelativeTime(channel.last_message.created_at)}
          </span>
        )}
        {channel.unread_count > 0 && (
          <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-violet-500 text-white">
            {channel.unread_count}
          </span>
        )}
      </div>
    </button>
  )
}

// New DM Modal
function NewDMModal({ 
  initialUserId,
  onClose, 
  onSelect 
}: { 
  initialUserId?: number
  onClose: () => void
  onSelect: (channel: { id: string }) => void 
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isStarting, setIsStarting] = useState<number | null>(null)
  
  // Fetch users
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['teams-users'],
    queryFn: usersApi.list,
  })
  
  // Filter users
  const filteredUsers = useMemo(() => {
    const otherUsers = users.filter(u => !u.is_self)
    if (!searchQuery) return otherUsers
    const q = searchQuery.toLowerCase()
    return otherUsers.filter(u => 
      u.name?.toLowerCase().includes(q) || 
      u.email?.toLowerCase().includes(q)
    )
  }, [users, searchQuery])
  
  // Auto-start DM if initialUserId
  useMemo(() => {
    if (initialUserId && !isStarting) {
      handleStartDM(initialUserId)
    }
  }, [initialUserId])
  
  const handleStartDM = async (userId: number) => {
    setIsStarting(userId)
    try {
      const result = await dmApi.getOrCreate(userId)
      onSelect({ id: result.id })
    } catch (e) {
      console.error('Failed to start DM:', e)
    } finally {
      setIsStarting(null)
    }
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-white">New Message</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search people..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              autoFocus
            />
          </div>
        </div>
        
        <div className="max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-violet-500" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-slate-400">No users found</p>
            </div>
          ) : (
            <div className="p-2">
              {filteredUsers.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleStartDM(user.id)}
                  disabled={isStarting === user.id}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left"
                >
                  <div className="relative">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold bg-gradient-to-br",
                      getAvatarColor(user.name)
                    )}>
                      {getInitials(user.name, user.email)}
                    </div>
                    <div className={cn(
                      "absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white dark:ring-slate-800",
                      getPresenceConfig(user.is_online ? 'online' : 'offline').color
                    )} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 dark:text-white truncate">{user.name}</p>
                    <p className="text-xs text-slate-400 truncate">{user.email}</p>
                  </div>
                  {isStarting === user.id ? (
                    <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
